/**
 * Routing. Every navigation entry gets a destination from the same model the
 * sidebar reads. The guard below is presentation, not access control: every
 * request is authorized server-side, and deep-linking past it gains a 401/403.
 */

import type { ComponentType, ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { NAV_ENTRIES } from '@/navigation/navigation';
import { AccountPage } from '@/routes/account/AccountPage';
import { IntegrationsPage } from '@/routes/integrations/IntegrationsPage';
import { AdvisorListPage } from '@/routes/advisor/AdvisorListPage';
import { AdvisorRunPage } from '@/routes/advisor/AdvisorRunPage';
import { UnmatchedDemandPage } from '@/routes/advisor/UnmatchedDemandPage';
import { AiUsagePage } from '@/routes/ai-usage/AiUsagePage';
import { AssetEditPage } from '@/routes/assets/editor/AssetEditPage';
import { CatalogPage } from '@/routes/assets/list/CatalogPage';
import { NewAssetPage } from '@/routes/assets/NewAssetPage';
import { AssetPage } from '@/routes/assets/page/AssetPage';
import { RefinePage } from '@/routes/assets/refine/RefinePage';
import { AuditLogPage } from '@/routes/audit-log/AuditLogPage';
import { ConfigurationPage } from '@/routes/configuration/ConfigurationPage';
import { DashboardPage } from '@/routes/dashboard/DashboardPage';
import { FeedbackDetailPage } from '@/routes/feedback/FeedbackDetailPage';
import { FeedbackPage } from '@/routes/feedback/FeedbackPage';
import { BusinessOpportunitiesPage } from '@/routes/business-opportunities/BusinessOpportunitiesPage';
import { BusinessOpportunityDetailPage } from '@/routes/business-opportunities/BusinessOpportunityDetailPage';
import { IndustriesPage } from '@/routes/industries/IndustriesPage';
import { useOpenSubmitIdea } from '@/routes/business-opportunities/SubmitIdeaDialog';
import { NotAvailable, NotFound } from '@/routes/NotFound';
import { ConnectPage } from '@/routes/connect/ConnectPage';
import { safeNext, SignIn } from '@/routes/SignIn';
import { SignInLocal } from '@/routes/SignInLocal';
import { UsersPage } from '@/routes/users/UsersPage';
import { WhatsNewPage } from '@/routes/whats-new/WhatsNewPage';
import { useSession } from '@/session/SessionContext';
import { AppShell } from '@/shell/AppShell';
import { NoAccess } from '@/shell/NoAccess';

const SCREENS: Readonly<Record<string, ComponentType>> = {
  dashboard: DashboardPage,
  'business-opportunities': BusinessOpportunitiesPage,
  assets: CatalogPage,
  advisor: AdvisorListPage,
  'unmatched-demand': UnmatchedDemandPage,
  industries: IndustriesPage,
  users: UsersPage,
  configuration: ConfigurationPage,
  'ai-usage': AiUsagePage,
  'audit-log': AuditLogPage,
  feedback: FeedbackPage,
  integrations: IntegrationsPage,
  'whats-new': WhatsNewPage,
};

/** An old link to the Submit an idea page: open the dialog over Business Opportunities. */
function SubmitIdeaRedirect() {
  const openSubmitIdea = useOpenSubmitIdea();
  useEffect(() => openSubmitIdea(), [openSubmitIdea]);
  return <Navigate to="/business-opportunities" replace />;
}

/** Ideas became Business Opportunities: old links keep their filters. */
function IdeasRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/business-opportunities${search}`} replace />;
}

function IdeaRedirect() {
  const { ideaId } = useParams();
  return <Navigate to={`/business-opportunities/${ideaId ?? ''}`} replace />;
}

/** A page outside the navigation that opens only for an entry the server granted. */
function Gate({ navKey, children }: { navKey: string; children: ReactNode }) {
  return useSession().canSee(navKey) ? children : <NotAvailable />;
}

/** Signed out: to /signin, carrying where the person was going. */
function ToSignIn() {
  const { pathname, search } = useLocation();
  const target = `${pathname}${search}`;
  const next = target === '/' ? '' : `?next=${encodeURIComponent(target)}`;
  return <Navigate to={`/signin${next}`} replace />;
}

/**
 * Signed in at a sign-in address: on to where the person was going (the password form signs in
 * in place, so this is how an AI tool's consent page comes back), else the dashboard, unless
 * signing out is under way.
 */
function SignedInAtSignIn() {
  const params = new URLSearchParams(useLocation().search);
  if (params.has('signed_out')) return null;
  return <Navigate to={safeNext(params.get('next')) ?? '/dashboard'} replace />;
}

function BootScreen({ error }: { error?: boolean }) {
  return (
    <div className="boot-screen">
      <div className="card boot-card">
        <div className="card-body">
          <div className="empty">
            <div className={`e-icon${error ? ' e-icon-warning' : ''}`}>
              {error ? <AlertTriangle aria-hidden="true" /> : <Loader2 aria-hidden="true" className="spin" />}
            </div>
            <h3>{error ? 'Wynxx Discover cannot reach its API' : 'Starting Wynxx Discover'}</h3>
            <p>{error ? 'Check that the backend is running and reachable, then reload.' : 'Loading your session.'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const { user, isLoading, error, canSee } = useSession();

  if (isLoading) return <BootScreen />;
  if (error) return <BootScreen error />;

  if (!user) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signin/local" element={<SignInLocal />} />
        <Route path="*" element={<ToSignIn />} />
      </Routes>
    );
  }

  // An account with no role (created by its first Microsoft sign-in) may do nothing yet.
  if (user.roles.length === 0) {
    return (
      <Routes>
        <Route path="/signin/*" element={<SignedInAtSignIn />} />
        <Route path="*" element={<NoAccess />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/signin/*" element={<SignedInAtSignIn />} />
      {/* An AI tool's consent: one question, out of the shell. */}
      <Route path="/connect" element={<ConnectPage />} />
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        {NAV_ENTRIES.map((entry) => {
          // An entry without a path opens a dialog in the shell; it has no route.
          if (entry.path === null) return null;
          const Screen = SCREENS[entry.key] ?? NotFound;
          return (
            <Route
              key={entry.key}
              path={entry.path}
              element={canSee(entry.key) ? <Screen /> : <NotAvailable />}
            />
          );
        })}
        {/* An idea's page sits under Business Opportunities, outside the navigation. */}
        <Route
          path="/business-opportunities/:ideaId"
          element={canSee('business-opportunities') ? <BusinessOpportunityDetailPage /> : <NotAvailable />}
        />
        {/* An asset's page, its editor and New asset sit under the catalog, outside the navigation. */}
        <Route path="/assets/new" element={<Gate navKey="assets"><NewAssetPage /></Gate>} />
        <Route path="/assets/:assetId" element={<Gate navKey="assets"><AssetPage /></Gate>} />
        <Route path="/assets/:assetId/edit" element={<Gate navKey="assets"><AssetEditPage /></Gate>} />
        <Route path="/assets/:assetId/refine/:extractionId" element={<Gate navKey="assets"><RefinePage /></Gate>} />
        {/* A run's result page sits under the Asset Advisor, outside the navigation. */}
        <Route path="/advisor/:runId" element={<Gate navKey="advisor"><AdvisorRunPage /></Gate>} />
        {/* A request's page sits under Feedback, outside the navigation. */}
        <Route path="/feedback/:feedbackId" element={canSee('feedback') ? <FeedbackDetailPage /> : <NotAvailable />} />
        {/* An earlier release's notes sit under What's new, outside the navigation. */}
        <Route path="/whats-new/:version" element={<Gate navKey="whats-new"><WhatsNewPage /></Gate>} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/ideas/new" element={canSee('submit-idea') ? <SubmitIdeaRedirect /> : <NotAvailable />} />
        <Route path="/ideas" element={<IdeasRedirect />} />
        <Route path="/ideas/:ideaId" element={<IdeaRedirect />} />
        {/* AI models moved into a Configuration tab. */}
        <Route path="/ai-models" element={<Navigate to="/configuration?tab=ai-models" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
